import { BodyM, HeadingSBold, Caption, BodyS } from 'components/Typography/Typography';
import React, { useState } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/billingPaymentTrack/billingPaymentTrackStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import FileUpload, { FileUploadError, MultipleFilesUploadError } from 'components/CustomUpload/FileUpload';
import CustomLink from 'components/CustomLink/CustomLink';
import VerticalAlignBottom from '@material-ui/icons/VerticalAlignBottom';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import { XLSFromFileToJSON } from 'utils/export/export';
import ExcelPreviewDialog from 'views/BillingPaymentTrack/subcomponents/ExcelPreviewDialog';
import { EXCEL_FILE, INVOICE_TYPE } from 'views/BillingPaymentTrack/commons/constants';
import UploadedFile from 'views/BillingPaymentTrack/subcomponents/UploadedFile';
import { isExcelFileValid } from 'views/BillingPaymentTrack/helpers/isExcelFileValid';

// @ts-ignore
const useStyles = makeStyles(styles);

const SAMPLE_ACCOUNT_EXCEL_URL = '/templates/BillingPaymentTrackAccountTemplate.xlsx';
const SAMPLE_INVOICE_EXCEL_URL = '/templates/BillingPaymentTrackInvoiceTemplate.xlsx';

interface IUploadExcelFile {
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<FormData>>;
	invoiceType: string;
}

const MAX_NUMBER_OF_FILES = 1;

const UploadExcelFile = ({ formData, setFormData, invoiceType }: IUploadExcelFile) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [multipleFilesErrors, setMultipleFilesErrors] = useState<Array<MultipleFilesUploadError>>([]);
	const [excelPreviewData, setExcelPreviewData] = useState<Object[]>([{}]);
	const [previewDialogIsOpen, setPreviewDialogIsOpen] = useState(false);

	const uploadDisabled = formData.has(EXCEL_FILE);

	const removeEntry = () => {
		setMultipleFilesErrors([]);
		setFormData((formData) => {
			const updatedFormData = new FormData();
			for (const singleEntry of formData.entries()) {
				updatedFormData.append(...singleEntry);
			}
			updatedFormData.delete(EXCEL_FILE);
			return updatedFormData;
		});
	};

	const fileUploaded = (result: FormData) => {
		setLoading(true);
		const errors: MultipleFilesUploadError[] = [];
		const resultsArray = Array.from(result.entries());
		if (resultsArray.length > 1) {
			for (let i = 1; i < resultsArray.length; i++) {
				errors.push({ fileName: resultsArray[i][0], fileUploadError: FileUploadError.TOO_MANY_FILES });
			}
			setMultipleFilesErrors((_errors) => _errors.concat(errors));
		}
		if (resultsArray.length) {
			const file = resultsArray[0][1] as File;
			const fileName = resultsArray[0][0];
			parseExcel(fileName, file);
		} else {
			setLoading(false);
		}
	};

	const parseExcel = (fileName: string, file: File) => {
		XLSFromFileToJSON(file).then((jsonFile) => {
			isExcelFileValid(jsonFile, invoiceType).then((excelIsValid) => {
				if (excelIsValid.valid) {
					setExcelPreviewData(jsonFile);
					setFormData((formData) => {
						const updatedFormData = new FormData();
						for (const singleEntry of formData.entries()) {
							updatedFormData.append(...singleEntry);
						}
						updatedFormData.append(EXCEL_FILE, file);
						return updatedFormData;
					});
				} else {
					const error =
						excelIsValid.error === 'accountNumber'
							? FileUploadError.INVALID_ACCOUNT_NUMBER
							: FileUploadError.BAD_SHEET;
					setMultipleFilesErrors((errors) => {
						return errors.concat([
							{
								fileName,
								fileUploadError: error,
							},
						]);
					});
				}
			});
			setLoading(false);
		});
	};

	const openExcelPreviewDialog = () => {
		setPreviewDialogIsOpen(true);
	};

	return (
		<div className={classes.smallTab}>
			<div className={classes.tab}>
				<HeadingSBold>{translate('billing.payment.track.upload.excel.file')}</HeadingSBold>
				<BodyM>{translate('billing.payment.upload.excel.files.reason')}</BodyM>
				<div className={classes.uploadProofWrapper}>
					<ConditionalRender
						show={uploadDisabled}
						onFalse={() => (
							<>
								<FileUpload
									textSize="subtext"
									text={translate('billing.payment.track.upload.excel.max.one')}
									subtext={translate('billing.payment.track.upload.drag.or.drop')}
									onFileChange={fileUploaded}
									loadingText={translate('billing.payment.track.uploading.file')}
									loading={loading}
									errorMessage={''}
									acceptedExtensions={[
										'application/vnd.ms-excel',
										'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
									]}
									multipleFilesAllowed={true}
									setMultipleFilesErrors={setMultipleFilesErrors}
									uploadDisabled={uploadDisabled}
								/>
								<ConditionalRender
									show={loading}
									onFalse={() => (
										<>
											<div className={classes.allowedFormats}>
												<Caption>
													{translate('billing.payment.track.upload.excel.allowed.formats')}
												</Caption>
											</div>
											<div className={classes.fileUploadHeader}>
												<BodyS disableMargin>
													{translate('dialog.how.file.needs.to.look.like')}
												</BodyS>
											</div>
											<CustomLink
												color="brand"
												to={
													invoiceType === INVOICE_TYPE.ACCOUNT
														? SAMPLE_ACCOUNT_EXCEL_URL
														: SAMPLE_INVOICE_EXCEL_URL
												}
												download
												target="_blank"
												type="bodyS"
											>
												<div className={classes.downloadLink}>
													<VerticalAlignBottom />
													{translate('billing.payment.track.download.sample')}
												</div>
											</CustomLink>
										</>
									)}
								/>
							</>
						)}
						onTrue={() => (
							<div className={classes.uploadProofFiles}>
								<UploadedFile
									name={(formData.get(EXCEL_FILE) as File).name}
									removeEntry={removeEntry}
									showPreview={openExcelPreviewDialog}
								/>
							</div>
						)}
					/>
					<div>
						{multipleFilesErrors.map((error) => (
							<div className={classes.errorBox} key={error.fileName + 'err'}>
								<ErrorBox
									text={translate(
										`billing.payment.track.upload.error.${error.fileUploadError.toLowerCase()}${
											error.fileUploadError === FileUploadError.TOO_MANY_FILES &&
											MAX_NUMBER_OF_FILES === 1
												? '.one.file'
												: ''
										}`,
										error.fileName,
										MAX_NUMBER_OF_FILES
									)}
								/>
							</div>
						))}
					</div>
				</div>
				<ExcelPreviewDialog
					hideDialog={() => setPreviewDialogIsOpen(false)}
					isOpen={previewDialogIsOpen}
					excelPreviewData={excelPreviewData}
					invoiceType={invoiceType}
				/>
			</div>
		</div>
	);
};

export default UploadExcelFile;
